import { useEffect, useRef } from "react";
import { Box, LoadingOverlay, Table, Text } from "@mantine/core";
import dayjs from "dayjs";
import { toast } from "react-toastify";

import { getAxiosResponseError } from "../../utils/functions";
import { AppPagination } from "../AppPagination/AppPagination";
import { TableSkeletonRules } from "../Skeletons/TableSkeletonRules";
import { TableNoResults } from "../TableNoResults/TableNoResults";
import {
  useRuleColumnsDataQuery,
  useRuleColumnsQuery,
} from "./useRuleColumnsData";
import classes from "./RuleColumnsTable.module.scss";

export default function RuleColumnsTable({
  ruleId,
  page,
  take,
  onPageChange,
  onTakeChange,
  enabled,
  nonce = 0,
}) {
  const { data: columnNames, error: columnsError } = useRuleColumnsQuery({
    ruleId,
    nonce,
    enabled,
  });

  const {
    data,
    isLoading,
    isFetching,
    error: dataError,
  } = useRuleColumnsDataQuery({
    ruleId,
    page,
    take,
    nonce,
    enabled,
  });

  const error = columnsError || dataError;

  const lastToastedNonceRef = useRef(0);
  useEffect(() => {
    if (!enabled || !error) return;
    if (lastToastedNonceRef.current === nonce) return;
    lastToastedNonceRef.current = nonce;
    toast.error(getAxiosResponseError(error) || "Error loading data");
  }, [enabled, error, nonce]);

  if (!enabled) {
    return null;
  }

  if (error) {
    return (
      <Box className={classes.box} ta="center" py="md">
        <Text c="red">
          {getAxiosResponseError(error) || "Error loading data"}
        </Text>
      </Box>
    );
  }

  const cols = columnNames || [];
  const rows = data?.rows || [];
  const totalCount = data?.totalCount ?? 0;

  const formatCell = (value, columnName) => {
    if (value == null || value === "") return "";
    if (
      typeof columnName === "string" &&
      columnName.toLowerCase().includes("date")
    ) {
      const m = dayjs(value);
      return m.isValid() ? m.format("M/D/YYYY, h:mm A") : String(value);
    }
    return String(value);
  };

  return (
    <Box className={classes.box} pos="relative">
      <LoadingOverlay
        visible={isFetching && !isLoading}
        overlayProps={{ blur: 2 }}
      />
      <div className={classes.tableScroll}>
        <Table
          horizontalSpacing="md"
          stickyHeader
          highlightOnHover
          className={classes.table}
        >
          <Table.Thead>
            <Table.Tr>
              {cols.map((c, i) => (
                <Table.Th key={`${c}-${i}`}>{c}</Table.Th>
              ))}
            </Table.Tr>
          </Table.Thead>
          {isLoading ? (
            <TableSkeletonRules length={take} />
          ) : (
            <Table.Tbody>
              {rows.map((row, i) => (
                <Table.Tr key={i}>
                  {cols.map((c, j) => (
                    <Table.Td key={`${c}-${j}`}>{formatCell(row?.[c], c)}</Table.Td>
                  ))}
                </Table.Tr>
              ))}

              {rows.length === 0 && (
                <TableNoResults colSpan={cols.length || 1} />
              )}
            </Table.Tbody>
          )}
        </Table>
      </div>

      {totalCount > 0 && (
        <AppPagination
          page={page}
          take={take}
          total={totalCount}
          onPageChange={(p) => onPageChange(p - 1)}
          onTakeChange={onTakeChange}
        />
      )}
    </Box>
  );
}
